import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Users,
  Receipt,
  LogOut,
  X,
  FileText,
  History,
  Settings,
  KeyRound,
  UserCog,
  FileStack,
  MessageSquare,
  FileBarChart,
  Wallet,
  Bell,
  ShieldCheck,
  MapPin,
  ClipboardCheck,
  BanknoteIcon,
  TrendingUp,
  Coins,
  Gavel,
  Eye,
  Key,
  Shield,
  QrCode,
  FileSignature,
  MessagesSquare,
  GraduationCap,
  Wrench,
  ClipboardList,
  Printer,
  Crown,
  Smartphone,
  Megaphone,
  Package,
  ChevronRight,
  BarChart3,
  Briefcase,
  Scale,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { agencySettingsAPI } from '../../lib/api';
import api from '../../lib/api';
import { useAccess } from '../../context/AccessContext';
import { toast } from 'sonner';
import { enablePushForCurrentUser } from '../../lib/push';
import { BellRing, Handshake, Network, Contact } from 'lucide-react';
import { useActionCounters } from '../../context/ActionCountersContext';

// Groupage logique de la navigation (UX professionnelle)
const navSections = [
  {
    title: 'Pilotage',
    items: [
      { path: '/', icon: LayoutDashboard, label: 'Tableau de bord' },
      { path: '/agency-stats', icon: BarChart3, label: "Statistiques de l'agence" },
    ],
  },
  {
    title: 'Exploitation',
    items: [
      { path: '/properties', icon: Building2, label: 'Biens' },
      { path: '/clients', icon: Users, label: 'Clients' },
      { path: '/tenant-situations', icon: FileBarChart, label: 'Situation Locataires' },
      { path: '/transactions', icon: Receipt, label: 'Transactions' },
      { path: '/land-sales', icon: MapPin, label: 'Ventes Terrains' },
      { path: '/property-inspections', icon: ClipboardCheck, label: 'Etats des Lieux' },
      { path: '/property-visits', icon: Eye, label: 'Visites de Biens' },
      { path: '/key-handovers', icon: Key, label: 'Remise de Clés' },
      { path: '/repairs', icon: Wrench, label: 'Reparations' },
      { path: '/repair-requests', icon: ClipboardList, label: 'Demandes Reparations' },
      { path: '/parcel-verifications', icon: FileSignature, label: 'Dossiers Parcelle' },
      { path: '/printable-forms', icon: Printer, label: 'Formulaires Imprimables' },
      { path: '/construction', icon: Building2, label: 'Construction' },
    ],
  },
  {
    title: 'Finance',
    items: [
      { path: '/rent-payments', icon: FileText, label: 'Paiements Loyer' },
      { path: '/moratoriums', icon: Handshake, label: 'Moratoires' },
      { path: '/rent-increases', icon: TrendingUp, label: 'Modifications Loyer' },
      { path: '/due-notices', icon: Bell, label: 'Avis Echeance' },
      { path: '/all-receipts', icon: FileStack, label: 'Toutes les Quittances' },
      { path: '/agency-expenses', icon: Receipt, label: 'Frais d\'agence' },
      { path: '/financial-statements', icon: FileBarChart, label: 'Releves Financiers' },
      { path: '/owner-balance', icon: Wallet, label: 'Bilan Proprietaires' },
      { path: '/agency-balance', icon: TrendingUp, label: 'Bilan Agence' },
      { path: '/security-deposits', icon: ShieldCheck, label: 'Depots de Garantie' },
      { path: '/agent-deposits', icon: BanknoteIcon, label: 'Versements Agents' },
    ],
  },
  {
    title: 'Ressources Humaines',
    items: [
      { path: '/users', icon: UserCog, label: 'Utilisateurs', adminOnly: true },
      { path: '/my-hr', icon: Briefcase, label: 'Mon Espace RH' },
      { path: '/staff-management', icon: Contact, label: 'Gestion du personnel', adminOnly: true },
      { path: '/tenant-notices', icon: Scale, label: 'Journal Contentieux' },
      { path: '/tasks', icon: ClipboardCheck, label: 'Taches' },
      { path: '/shareholders', icon: Coins, label: 'Actionnariat' },
      { path: '/general-assemblies', icon: Gavel, label: 'Assemblees Generales' },
      { path: '/agent-permissions', icon: Shield, label: "Droits d'accès", adminOnly: true },
      { path: '/training', icon: GraduationCap, label: 'Formation' },
    ],
  },
  {
    title: 'Portails & Communication',
    items: [
      { path: '/tenant-accounts', icon: KeyRound, label: 'Acces Locataires' },
      { path: '/owner-access', icon: Building2, label: 'Acces Proprietaires' },
      { path: '/tenant-requests', icon: MessageSquare, label: 'Demandes Locataires' },
      { path: '/chat', icon: MessagesSquare, label: 'Messagerie' },
    ],
  },
  {
    title: 'Configuration',
    items: [
      { path: '/my-subscription', icon: Crown, label: 'Mon abonnement' },
      { path: '/agency-settings', icon: Settings, label: 'Parametres' },
      { path: '/document-templates', icon: FileSignature, label: 'Modeles Documents' },
      { path: '/confidentiality', icon: ShieldCheck, label: 'Clause Confidentialite' },
      { path: '/qr-codes', icon: QrCode, label: 'QR Codes' },
      { path: '/activity-logs', icon: History, label: 'Tracabilite' },
    ],
  },
  {
    title: 'Marketing',
    items: [
      { path: '/marketing-contracts', icon: Megaphone, label: 'Contrats Publicité', superAdminOnly: true },
      { path: '/shipments', icon: Package, label: 'Module Colis' },
      { path: '/partnerships', icon: Network, label: 'Partenariats' },
    ],
  },
  {
    title: 'Super-Admin',
    items: [
      { path: '/agencies-admin', icon: Shield, label: 'Gestion Agences', superAdminOnly: true },
      { path: '/admin/mobile-payments', icon: Smartphone, label: 'Paiements Mobile Money', superAdminOnly: true },
    ],
  },
];

export const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [agencySettings, setAgencySettings] = useState(null);
  const { isAllowed, moduleForPath } = useAccess();
  const [unreadChat, setUnreadChat] = useState(0);
  const [pendingTasks, setPendingTasks] = useState(0);
  const { total: actionTotal, perRoute: actionPerRoute } = useActionCounters();
  const knownTaskIdsRef = useRef(null);
  const [pushPermission, setPushPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );

  const handleEnablePush = async () => {
    const res = await enablePushForCurrentUser({ silent: false });
    if (res.ok) {
      toast.success('Notifications activees sur cet appareil');
      setPushPermission('granted');
    } else if (res.reason === 'permission-denied') {
      toast.error('Vous avez bloque les notifications. Modifiez les permissions dans votre navigateur.');
      setPushPermission('denied');
    } else if (res.reason === 'unsupported') {
      toast.error('Votre navigateur ne supporte pas les notifications push');
    } else {
      toast.error('Activation impossible (' + (res.reason || 'erreur') + ')');
    }
  };

  useEffect(() => {
    const fetchAgencySettings = async () => {
      try {
        const response = await agencySettingsAPI.get();
        setAgencySettings(response.data);
      } catch (error) {
        console.error('Error fetching agency settings:', error);
      }
    };
    fetchAgencySettings();
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const r = await api.get('/chat/unread-count');
        if (!cancelled) setUnreadChat(r.data?.unread || 0);
      } catch (e) {
        if (!cancelled) setUnreadChat(0);
      }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  // Poll for current user's tasks: badge count + toast on new task assigned
  useEffect(() => {
    if (!user) return;
    if (!['admin', 'super_admin', 'agent'].includes(user.role)) return;
    let cancelled = false;

    const fetchTasks = async () => {
      try {
        const r = await api.get(`/tasks?assigned_to=${encodeURIComponent(user.id)}`);
        if (cancelled) return;
        const tasks = r.data || [];
        const pending = tasks.filter(t => t.status !== 'termine');
        setPendingTasks(pending.length);

        const currentIds = new Set(tasks.map(t => t.id));
        if (knownTaskIdsRef.current === null) {
          knownTaskIdsRef.current = currentIds;
          return;
        }
        const newTasks = tasks.filter(
          t => !knownTaskIdsRef.current.has(t.id) && t.created_by !== user.id
        );
        newTasks.forEach(t => {
          toast(`Nouvelle tache : ${t.title}`, {
            description: `Assignee par ${t.created_by_name || 'l\'administration'} - Priorite : ${t.priority}`,
            duration: 8000,
            action: {
              label: 'Voir',
              onClick: () => navigate('/tasks'),
            },
          });
        });
        knownTaskIdsRef.current = currentIds;
      } catch (e) {
        // non-blocking
      }
    };
    fetchTasks();
    const id = setInterval(fetchTasks, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, navigate]);

  // Presence ping
  useEffect(() => {
    if (!user) return;
    if (!['admin', 'super_admin', 'agent'].includes(user.role)) return;
    const ping = () => api.post('/chat/presence/ping').catch(() => {});
    ping();
    const id = setInterval(ping, 30000);
    return () => clearInterval(id);
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userRole = user?.role;
  const isAdminOrSuper = userRole === 'admin' || userRole === 'super_admin';

  // Filter items per section based on permissions
  const filterItems = (items) => items.filter((item) => {
    if (item.adminOnly && !isAdminOrSuper) return false;
    if (item.superAdminOnly && userRole !== 'super_admin') return false;
    if (!isAllowed(moduleForPath(item.path))) return false;
    return true;
  });

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={`mobile-overlay lg:hidden ${isOpen ? 'active' : ''}`}
        onClick={onClose}
      />

      {/* Sidebar */}
      <aside className={`sidebar ${isOpen ? 'sidebar-open' : ''} lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          {/* Header brand */}
          <div className="relative overflow-hidden ae-wax-bg" style={{
            background: 'linear-gradient(135deg, #131E15 0%, #2B3A2C 55%, #3F5240 100%)',
          }}>
            {/* copper ambient glow */}
            <div className="absolute inset-0 opacity-40 pointer-events-none" style={{
              background: 'radial-gradient(circle at 90% 10%, rgba(184,92,56,0.55) 0%, transparent 55%)',
            }} />
            {/* fine copper grid */}
            <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
              backgroundImage: 'linear-gradient(#B85C38 1px, transparent 1px), linear-gradient(90deg, #B85C38 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }} />

            <div className="relative flex items-center justify-between p-5">
              <div className="flex items-center gap-3 min-w-0">
                {agencySettings?.logo_url ? (
                  <div className="w-11 h-11 rounded-xl bg-white p-1 shadow-lg ring-1 ring-[#B85C38]/40 flex-shrink-0">
                    <img
                      src={agencySettings.logo_url.startsWith('http') ? agencySettings.logo_url : `${process.env.REACT_APP_BACKEND_URL}${agencySettings.logo_url}`}
                      alt={agencySettings?.name || 'Logo'}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        if (e.target.parentElement?.nextSibling) e.target.parentElement.nextSibling.style.display = 'flex';
                      }}
                    />
                  </div>
                ) : null}
                <div
                  className={`w-11 h-11 rounded-xl items-center justify-center shadow-lg flex-shrink-0 ${agencySettings?.logo_url ? 'hidden' : 'flex'}`}
                  style={{ background: 'linear-gradient(135deg, #B85C38 0%, #D07A55 100%)' }}
                >
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h1 className="font-bold text-white tracking-tight text-sm leading-tight truncate font-display">
                    {agencySettings?.name || "GAB'S SERVICES"}
                  </h1>
                  <p className="text-[10px] text-[#D4A017] font-semibold uppercase tracking-[0.14em] mt-0.5 truncate">
                    {agencySettings?.slogan || 'Agence Immobiliere'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="lg:hidden p-2 hover:bg-white/10 rounded-lg text-white/80 transition-colors"
                data-testid="close-sidebar-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Quick actions */}
          <div className="px-4 pt-4 space-y-2">
            {pushPermission === 'default' && (
              <Button
                variant="ghost"
                className="w-full justify-start text-[#142D3A] hover:text-[#142D3A] hover:bg-[#D4A745]/15 border border-[#D4A745]/40 bg-gradient-to-r from-[#D4A745]/8 to-transparent rounded-lg font-medium text-[0.85rem] h-9"
                onClick={handleEnablePush}
                data-testid="enable-push-btn"
              >
                <BellRing className="w-4 h-4 mr-2 text-[#D4A745]" />
                Activer les notifications
              </Button>
            )}
            <Button
              variant="ghost"
              className="w-full justify-start text-gray-500 hover:text-red-600 hover:bg-red-50/70 border border-gray-200/70 rounded-lg font-medium text-[0.85rem] h-9 transition-colors"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>

          {/* Navigation - Scrollable, grouped in sections */}
          <nav className="flex-1 px-3 pt-3 pb-4 overflow-y-auto min-h-0">
            {navSections.map((section) => {
              const visibleItems = filterItems(section.items);
              if (visibleItems.length === 0) return null;
              return (
                <div key={section.title} className="mb-1">
                  <div className="sidebar-section">{section.title}</div>
                  <div className="space-y-0.5">
                    {visibleItems.map((item) => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        onClick={onClose}
                        className={({ isActive }) => `nav-link group ${isActive ? 'active' : ''}`}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                        end={item.path === '/'}
                      >
                        <item.icon className="w-[18px] h-[18px]" />
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.path === '/chat' && unreadChat > 0 && (
                          <span
                            className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold shadow-sm"
                            data-testid="chat-unread-badge"
                          >
                            {unreadChat > 99 ? '99+' : unreadChat}
                          </span>
                        )}
                        {item.path === '/tasks' && pendingTasks > 0 && (
                          <span
                            className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold animate-pulse shadow-sm"
                            data-testid="tasks-pending-badge"
                            title={`${pendingTasks} tache(s) en attente`}
                          >
                            {pendingTasks > 99 ? '99+' : pendingTasks}
                          </span>
                        )}
                        {(() => {
                          const cnt = actionPerRoute[item.path] || 0;
                          const dashboardTotal = item.path === '/' ? actionTotal : 0;
                          const finalCount = cnt || dashboardTotal;
                          if (!finalCount || item.path === '/chat' || item.path === '/tasks') return null;
                          return (
                            <span
                              className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold shadow-sm"
                              data-testid={`action-badge-${item.path.replace(/\W+/g, '-')}`}
                              title={`${finalCount} action(s) a effectuer`}
                            >
                              {finalCount > 99 ? '99+' : finalCount}
                            </span>
                          );
                        })()}
                        <ChevronRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-50 group-hover:translate-x-0 transition-all text-[#D4A745]" />
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-3 border-t border-gray-100/80 bg-gradient-to-b from-transparent to-[#FBFAF6]">
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white shadow-sm border border-gray-100 hover:border-[#D4A745]/30 transition-colors">
              <div
                className="relative w-10 h-10 rounded-full flex items-center justify-center shadow-md ring-2 ring-white flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #D4A745 0%, #F2A900 100%)' }}
              >
                <span className="text-[#142D3A] font-bold text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-white" title="En ligne" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[0.825rem] font-semibold text-[#142D3A] truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {user?.name || 'Utilisateur'}
                </p>
                <p className="text-[10px] font-mono text-[#D4A745] font-bold tracking-wider uppercase">
                  {user?.employee_id || user?.role || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};
